import React from 'react';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { BillDetailRow, BillSortKey } from '../api';
import { Skeleton } from 'components/ui/skeleton';

interface Props {
    data: BillDetailRow[];
    loading: boolean;
    customerName: string;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: BillSortKey;
    sortOrder: 'asc' | 'desc';
    onSortChange: (col: BillSortKey) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onBack: () => void;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
}

const columns: Array<{
    key: BillSortKey | string;
    label: string;
    isNumeric: boolean;
}> = [
        { key: 'reference_no', label: 'Reference No', isNumeric: false },
        { key: 'branch_name', label: "Branch Name", isNumeric: false },
        { key: 'bill_date', label: 'Bill Date', isNumeric: false },
        { key: 'due_date', label: 'Due Date', isNumeric: false },
        { key: 'overdue_days', label: 'Overdue Days', isNumeric: true },
        { key: 'outstanding', label: 'Outstanding', isNumeric: true },
        { key: 'collection_responsibility', label: 'Collection Responsibility', isNumeric: false },
    ];

const BillDetailsTable: React.FC<Props> = ({
    data,
    loading,
    customerName,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onBack,
    conversionType,
    onConversionChange
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const renderLoadingRows = () =>
        Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border border-gray-200 px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    const formatNumber = (value: number): string => {
        let displayValue = value;

        switch (conversionType) {
            case 'Lakhs':
                displayValue = value / 100000;
                break;
            case 'Crores':
                displayValue = value / 10000000;
                break;
            case 'INR':
            default:
                displayValue = value;
        }

        return displayValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    return (
        <div className="flex flex-col h-full min-h-0 gap-4 mt-6">
            <div className="flex items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                    >
                        ← Back
                    </button>
                    <h2 className="text-lg font-semibold">
                        Bills for {customerName}
                    </h2>
                </div>

                <div className="flex gap-2">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md ${conversionType === t
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <div className="w-full max-h-full overflow-auto border border-gray-200">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const isActive = sortBy === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key as BillSortKey)}
                                        className={`border border-gray-300 px-2 py-2 font-bold cursor-pointer select-none ${col.isNumeric ? 'text-center' : 'text-left'
                                            }`}
                                    >
                                        <div className="flex items-center justify-center gap-1">
                                            {col.label}
                                            {isActive ? (
                                                sortOrder === 'asc' ? (
                                                    <ChevronUp size={14} />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                )
                                            ) : (
                                                <ChevronUp size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            renderLoadingRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-20 text-gray-500">
                                    No bills found.
                                </td>
                            </tr>
                        ) : (
                            data.map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="border px-2 py-1.5">{row.reference_no}</td>
                                    <td className='border px-2 py-1.5'>{row.branch_name}</td>
                                    <td className="border px-2 py-1.5">{row.bill_date ?? '-'}</td>
                                    <td className="border px-2 py-1.5">{row.due_date ?? '-'}</td>
                                    <td className="border px-2 py-1.5 text-center">
                                        {row.overdue_days ?? '-'}
                                    </td>
                                    <td className="border px-2 py-1.5 text-right">
                                        {formatNumber(row.outstanding)}
                                    </td>
                                    <td className="border px-2 py-1.5">
                                        {row.collection_responsibility ?? '-'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between shrink-0 bg-white pt-4">
                <span className="text-sm text-gray-600">
                    Showing{' '}
                    <strong>{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to{' '}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{' '}
                    <strong>{totalItems}</strong> results
                </span>

                <div className="flex items-center gap-10 text-sm">
                    <div className="flex items-center gap-1">
                        <span className="text-gray-600 mr-1">Rows per page</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <span className="text-gray-600">
                        Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                    </span>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>

                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronRight size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillDetailsTable;
